/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState } from 'react';
import { API_BASE_URL } from '../utils/apiBaseUrl.ts';
import { getAuth } from 'firebase/auth';

interface ShopifySyncButtonProps {
  isAdmin: boolean;
}

export default function ShopifySyncButton({ isAdmin }: ShopifySyncButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    totalCount: number;
    dateRange?: {
      oldest: string;
      newest: string;
    };
    message: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSync = async () => {
    setIsLoading(true);
    setResult(null);
    setError(null);

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Not authenticated');
      }
// Don't show button if not admin
  if (!isAdmin) {
    console.log('Not showing button because isAdmin is false');
    return null;
  }

  console.log('Rendering the sync button!');
      // Get the current user's ID token
      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/.netlify/functions/shopify-manual-sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
      } else {
        setError(data.error || 'Sync failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't show button if not admin
  if (!isAdmin) {
    return null;
  }

  return (
   
    <div>
      {/* <h3 className="text-lg font-semibold mb-2">Shopify Full Sync</h3>
      <p className="text-sm text-gray-600 mb-4">
        Sync all historical orders from Shopify to Firestore. This may take a few minutes.
      </p> */}

      <button
        onClick={handleSync}
        disabled={isLoading}
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {isLoading ? 'Syncing...' : 'Run Full Sync'}
      </button>

      {isLoading && (
        <div className="mt-4 text-sm text-gray-600">
          <p>⏳ Syncing orders... This may take several minutes.</p>
          <p className="mt-1 text-xs">Please don't close this page.</p>
        </div>
      )}

      {result && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
          <p className="text-green-800 font-semibold">✅ Sync Completed!</p>
          <p className="text-sm text-green-700 mt-1">
            Total orders synced: {result.totalCount}
          </p>
          {result.dateRange && (
            <p className="text-xs text-green-600 mt-1">
              Date range: {new Date(result.dateRange.oldest).toLocaleDateString()} to{' '}
              {new Date(result.dateRange.newest).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-red-800 font-semibold">❌ Sync Failed</p>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      )}
    </div>

  );
}